import { get, post, put, del } from '../client/axiosClient';
import { holidayEndpoints } from '../endpoints/holiday.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse, PaginatedResponse, EmptyResponse, ID,
  Holiday, CreateHolidayRequest, UpdateHolidayRequest, HolidaySearchRequest,
} from '../types';

class HolidayService {
  async getAll(params?: HolidaySearchRequest): Promise<PaginatedResponse<Holiday>> {
    const endpoint = params
      ? buildEndpointWithQuery(holidayEndpoints.basePath(), params as Record<string, any>)
      : holidayEndpoints.list();
    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw?.data ?? {};
    return {
      success: raw?.success ?? false,
      message: raw?.message,
      data: pagedResult.items ?? [],
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  async getById(id: ID): Promise<ApiResponse<Holiday>> {
    const response = await get<ApiResponse<Holiday>>(holidayEndpoints.getById(id));
    return response.data;
  }

  async create(data: CreateHolidayRequest): Promise<ApiResponse<Holiday>> {
    const response = await post<ApiResponse<Holiday>>(holidayEndpoints.create(), data);
    return response.data;
  }

  async update(id: ID, data: UpdateHolidayRequest): Promise<ApiResponse<Holiday>> {
    const response = await put<ApiResponse<Holiday>>(holidayEndpoints.update(id), data);
    return response.data;
  }

  async delete(id: ID): Promise<EmptyResponse> {
    await del<void>(holidayEndpoints.delete(id));
    return { success: true };
  }

  /**
   * Bulk create holidays — creates one record per (officeId × holiday row).
   * Calls POST for each combination sequentially. Returns success/failed counts.
   */
  async createBulk(
    holidays: CreateHolidayRequest[]
  ): Promise<{ success: number; failed: number; errors: string[] }> {
    let success = 0;
    let failed = 0;
    const errors: string[] = [];

    for (const h of holidays) {
      try {
        await this.create(h);
        success++;
      } catch (err: any) {
        failed++;
        const apiBody = err?.response?.data;
        const msg = apiBody?.message || err?.message || 'Unknown error';
        errors.push(`${h.holidayDate} @ ${h.officeId}: ${msg}`);
      }
    }

    return { success, failed, errors };
  }
}

export const holidayService = new HolidayService();
export default holidayService;
